import { usePortal } from "@booking/hooks/usePortal";
import { JSX, useEffect, useState } from "react";

import useScrollLock from "@/hooks/useScrollLock";

import { useCYOQuery } from "../api/useCreateYourOwnQuery";
import { formatDate } from "../utils/dateUtils";
import CreateYourOwnErrorView from "./CreateYourOwnErrorView";
import CreateYourOwnIntroView from "./CreateYourOwnIntroView";
import styles from "./CreateYourOwnModal.module.css";
import CreateYourOwnPersonalForm, {
  CreateYourOwnPersonalFormData,
} from "./CreateYourOwnPersonalForm";
import CreateYourOwnSuccessView from "./CreateYourOwnSuccessView";
import { CreateYourOwnTravelFormData } from "./CreateYourOwnTravelForm";

interface CreateYourOwnModalProps {
  isOpen: boolean;
  courseNames?: string[];
  hotels?: Record<string, { name: string }>;
  venueName?: string;
  isEu?: boolean;
  onClose: () => void;
}

enum ModalState {
  TRAVEL_DETAILS = "travel-details",
  PERSONAL_DETAILS = "personal-details",
  SUCCESS = "success",
  ERROR = "error",
}

function useCreateYourOwnModal(props: CreateYourOwnModalProps) {
  const { isOpen, onClose } = props;
  const [modalState, setModalState] = useState<ModalState>(
    ModalState.TRAVEL_DETAILS,
  );
  const [travelDetails, setTravelDetails] =
    useState<CreateYourOwnTravelFormData | null>(null);
  const [personalDetails, setPersonalDetails] =
    useState<CreateYourOwnPersonalFormData | null>(null);
  useScrollLock(isOpen);

  useEffect(() => {
    // Reset modal state when modal is closed
    if (!isOpen) {
      setModalState(ModalState.TRAVEL_DETAILS);
      setTravelDetails(null);
      setPersonalDetails(null);
    }
  }, [isOpen]);

  return {
    isOpen,
    modalState,
    travelDetails,
    personalDetails,
    setModalState,
    setTravelDetails,
    setPersonalDetails,
    onClose,
  };
}

function CreateYourOwnModal(props: CreateYourOwnModalProps) {
  const {
    isOpen,
    modalState,
    travelDetails,
    personalDetails,
    setModalState,
    setTravelDetails,
    setPersonalDetails,
    onClose,
  } = useCreateYourOwnModal(props);
  const Portal = usePortal();
  const { courseNames = [], hotels = {}, venueName = "", isEu = false } = props;
  const { isSubmittingCYO, submitCYO } = useCYOQuery({
    onSuccess: () => setModalState(ModalState.SUCCESS),
    onError: () => setModalState(ModalState.ERROR),
  });

  const handleTravelDetailsContinue = (
    details: CreateYourOwnTravelFormData,
  ) => {
    setTravelDetails(details);
    setModalState(ModalState.PERSONAL_DETAILS);
  };

  const handlePersonalDetailsSubmit = async (
    details: CreateYourOwnPersonalFormData,
  ) => {
    if (!travelDetails) return;

    setPersonalDetails(details);

    const payload = {
      date: travelDetails.arrival_date,
      group_size: parseInt(travelDetails.group_size, 10),
      email: details.email,
      title: details.title,
      first_name: details.first_name,
      last_name: details.last_name,
      dialing_code: details.dialing_code,
      mobile: details.mobile,
      package_id: null, // Use null for general enquiries
      receive_offers: details.receive_offers,
      destination: null,
      nights: parseInt(travelDetails.nights, 10),
      rounds: parseInt(travelDetails.rounds, 10),
      include_flight_options: travelDetails.include_flight_options || false,
      flight_options: travelDetails.flight_options || null,
      additional_information: "",
      type: "venue_enquiry",
      courses: travelDetails.courses || null,
      hotels: hotels,
    };

    await submitCYO(payload);
  };

  const contentMap: Record<ModalState, JSX.Element> = {
    [ModalState.TRAVEL_DETAILS]: (
      <CreateYourOwnIntroView
        travelDetails={travelDetails}
        courseNames={courseNames}
        isEu={isEu}
        onContinue={handleTravelDetailsContinue}
      />
    ),
    [ModalState.PERSONAL_DETAILS]: (
      <CreateYourOwnPersonalForm
        initialData={personalDetails || undefined}
        isSubmitting={isSubmittingCYO}
        summaryInfo={
          travelDetails
            ? {
                date: formatDate(travelDetails.arrival_date),
                nights: travelDetails.nights,
                rounds: travelDetails.rounds,
                venueName: venueName,
              }
            : undefined
        }
        onSubmit={handlePersonalDetailsSubmit}
        onBack={() => setModalState(ModalState.TRAVEL_DETAILS)}
      />
    ),
    [ModalState.SUCCESS]: (
      <CreateYourOwnSuccessView
        travelDetails={travelDetails}
        personalDetails={personalDetails}
        venueName={venueName}
      />
    ),
    [ModalState.ERROR]: (
      <CreateYourOwnErrorView
        onRetry={() => setModalState(ModalState.TRAVEL_DETAILS)}
      />
    ),
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <div
        className={styles.modal}
        aria-modal="true"
        role="dialog"
        data-visibility={isOpen}
      >
        <div className={styles.overlay} onClick={onClose} />
        <div className={styles.modalContent}>
          {contentMap[modalState] ?? null}
        </div>
      </div>
    </Portal>
  );
}

export default CreateYourOwnModal;
export { CreateYourOwnModal, type CreateYourOwnModalProps };
